// Extracted from invoices/index.tsx to keep the grandfathered route shrinking
// while gaining the accessible <ConfirmDialog> on the three bulk
// window.confirm callsites (R17 polish batch). The hook owns the dialog
// state + the toast wiring; the route just calls handleX(ids).

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { trpc } from "@/lib/trpc";
import { type ReactElement, useState } from "react";
import { toast } from "sonner";

export type BulkAction = "skip" | "delete" | "push";

type Pending = {
	kind: BulkAction;
	count: number;
	run: () => Promise<void>;
} | null;

const COPY: Record<
	BulkAction,
	{ title: string; description: (n: number) => string; confirmLabel: string }
> = {
	skip: {
		title: "Mark invoices as already billed?",
		description: (n) =>
			`${n} invoice${n === 1 ? "" : "s"} will be marked as already billed outside the platform. This cannot be undone.`,
		confirmLabel: "Mark already billed",
	},
	delete: {
		title: "Delete draft invoices?",
		description: (n) =>
			`${n} draft invoice${n === 1 ? "" : "s"} will be deleted permanently. This cannot be undone.`,
		confirmLabel: "Delete",
	},
	push: {
		title: "Push invoices to QuickBooks?",
		description: (n) =>
			`${n} approved invoice${n === 1 ? "" : "s"} will be sent to the client's QuickBooks Online account.`,
		confirmLabel: "Push to QuickBooks",
	},
};

export function useBulkActions(opts: {
	selectedRows: { id: string }[];
	clearSelection: () => void;
	refresh: () => Promise<void>;
}): {
	handleApprove: () => Promise<void>;
	handleSkip: () => void;
	handleDelete: () => void;
	handlePush: () => void;
	approvePending: boolean;
	skipPending: boolean;
	deletePending: boolean;
	pushPending: boolean;
	bulkPending: boolean;
	dialog: ReactElement;
} {
	const [pending, setPending] = useState<Pending>(null);
	const approveMany = trpc.invoices.approveMany.useMutation();
	const skipMany = trpc.invoices.skipMany.useMutation();
	const deleteMany = trpc.invoices.deleteMany.useMutation();
	const pushMany = trpc.qbo.pushMany.useMutation();

	const ids = () => opts.selectedRows.map((r) => r.id);

	async function handleApprove() {
		const list = ids();
		try {
			const res = await approveMany.mutateAsync({ invoiceIds: list });
			toast.success(`Approved ${res.count} invoice${res.count === 1 ? "" : "s"}`);
			opts.clearSelection();
			await opts.refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to approve invoices");
		}
	}

	function queue(kind: BulkAction, run: () => Promise<void>) {
		setPending({ kind, count: ids().length, run });
	}

	function handleSkip() {
		const list = ids();
		queue("skip", async () => {
			try {
				const res = await skipMany.mutateAsync({ invoiceIds: list });
				toast.success(`Marked ${res.count} invoice${res.count === 1 ? "" : "s"} as already billed`);
				opts.clearSelection();
				await opts.refresh();
			} catch (err) {
				toast.error(err instanceof Error ? err.message : "Failed to skip invoices");
			}
		});
	}

	function handleDelete() {
		const list = ids();
		queue("delete", async () => {
			try {
				const res = await deleteMany.mutateAsync({ invoiceIds: list });
				toast.success(`Deleted ${res.count} invoice${res.count === 1 ? "" : "s"}`);
				opts.clearSelection();
				await opts.refresh();
			} catch (err) {
				toast.error(err instanceof Error ? err.message : "Failed to delete invoices");
			}
		});
	}

	function handlePush() {
		const list = ids();
		queue("push", async () => {
			try {
				const res = await pushMany.mutateAsync({ invoiceIds: list });
				if (res.failed === 0) {
					toast.success(
						`Pushed ${res.succeeded} invoice${res.succeeded === 1 ? "" : "s"} to QuickBooks`,
					);
				} else if (res.succeeded === 0) {
					toast.error(
						`Failed to push ${res.failed} invoice${res.failed === 1 ? "" : "s"} to QuickBooks`,
					);
				} else {
					toast.warning(
						`Pushed ${res.succeeded} of ${res.succeeded + res.failed}. ${res.failed} failed — check invoice details for errors.`,
					);
				}
				opts.clearSelection();
				await opts.refresh();
			} catch (err) {
				toast.error(err instanceof Error ? err.message : "Failed to push invoices");
			}
		});
	}

	const copy = pending ? COPY[pending.kind] : null;
	const bulkPending =
		approveMany.isPending || skipMany.isPending || deleteMany.isPending || pushMany.isPending;

	const dialog = (
		<ConfirmDialog
			open={pending !== null}
			onOpenChange={(open) => {
				if (!open) setPending(null);
			}}
			title={copy?.title ?? ""}
			description={copy && pending ? copy.description(pending.count) : ""}
			confirmLabel={copy?.confirmLabel ?? "Confirm"}
			destructive={pending?.kind === "delete"}
			isPending={bulkPending}
			onConfirm={async () => {
				if (!pending) return;
				const run = pending.run;
				setPending(null);
				await run();
			}}
		/>
	);

	return {
		handleApprove,
		handleSkip,
		handleDelete,
		handlePush,
		approvePending: approveMany.isPending,
		skipPending: skipMany.isPending,
		deletePending: deleteMany.isPending,
		pushPending: pushMany.isPending,
		bulkPending,
		dialog,
	};
}
